import { Component, OnInit } from '@angular/core';
import { StockTakeService } from './stock-take.service';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { InventoryService } from '../inventory/inventory.service';
import { UsersService } from '../users/users.service';
import { forkJoin } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PaginationComponent } from '../Common/pagination/pagination.component';
import { ConfigurationService, SETTING_KEYS } from '../configuration/configuration.service';

@Component({
  selector: 'app-stock-take-list',
  templateUrl: './stock-take.component.html',
  styleUrls: ['./stock-take.component.scss'],
  imports: [FormsModule, RouterModule, CommonModule, PaginationComponent],
})
export class StockTakeListComponent implements OnInit {
  // Existing Draft State
  stockTakes: any[] = [];
  loading = false;

  searchTerm = '';
  filterMode = '';
  pageSize = 10;
  currentPage = 1;
  pageCount = 1;

  // New Sessions State
  activeTab: 'sessions' | 'drafts' = 'sessions';
  sessions: any[] = []; // StockTakeSession[]
  selectedSessionIds: Set<string> = new Set();
  /**
   * Configuration > Stock Take > "Allow merging a single session". Merging is
   * what turns counting sessions into a MASTER and unlocks the variance
   * report; with this on, one session is enough. The backend enforces the
   * same rule.
   */
  allowSingleSessionMerge = false;
  get minMergeCount(): number {
    return this.allowSingleSessionMerge ? 1 : 2;
  }
  sessionFilter = {
    storeId: '',
    fromDate: null,
    toDate: null,
    status: '',
    page: 1,
    limit: 10,
  };
  sessionPageCount = 1;

  /**
   * Workflow view within the sessions tab — separates the merge pipeline:
   *  all       — everything (status dropdown active)
   *  toMerge   — REGULAR + IN_PROGRESS: submitted counts waiting to be merged
   *  toReview  — MASTER + IN_PROGRESS: merged result awaiting review/complete
   *  completed — COMPLETED: applied to inventory, done
   *  merged    — MERGED children (absorbed into a master, reference only)
   */
  sessionView: 'all' | 'toMerge' | 'toReview' | 'completed' | 'merged' = 'all';

  /**
   * Contextual status label: the raw IN_PROGRESS status means different
   * things for a device session (waiting to be merged) vs a master (waiting
   * to be reviewed/completed) — surface that instead of the raw enum, and
   * keep it aligned with the workflow tabs above the list.
   */
  statusLabel(s: any): string {
    if (s.status === 'IN_PROGRESS') {
      return s.sessionType === 'MASTER' ? 'TO REVIEW' : 'TO MERGE';
    }
    return s.status;
  }

  /**
   * Only a submitted device session (REGULAR + IN_PROGRESS) can be merged.
   * Drafts are still being counted, masters are already merged, and MERGED
   * children are absorbed originals.
   */
  canMerge(s: any): boolean {
    return s?.status === 'IN_PROGRESS' && s?.sessionType === 'REGULAR';
  }

  statusClass(s: any): string {
    if (s.status === 'IN_PROGRESS') {
      return s.sessionType === 'MASTER' ? 'bg-warning' : 'bg-info';
    }
    switch (s.status) {
      case 'DRAFT':
        return 'bg-primary';
      case 'COMPLETED':
        return 'bg-success';
      case 'MERGED':
        return 'bg-secondary';
      case 'CANCELLED':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  // Metadata Maps
  stores: any[] = [];
  storesMap = new Map<string, string>();
  usersMap = new Map<string, string>();

  // Preview State
  previewItems: any[] = [];
  previewLoading = false;

  constructor(
    private stockTakeService: StockTakeService,
    private toastr: ToastrService,
    private inventoryService: InventoryService,
    private usersService: UsersService,
    private modalService: NgbModal,
    private configurationService: ConfigurationService,
  ) { }

  ngOnInit(): void {
    this.configurationService
      .getValue(SETTING_KEYS.STOCKTAKE_ALLOW_SINGLE_SESSION_MERGE)
      .subscribe((value) => (this.allowSingleSessionMerge = value === true));

    // Scope the very first session list to the user's store so the list and the
    // merge action agree — merging requires all selected sessions to share one
    // store, and an unfiltered list would otherwise mix stores.
    const defaultStoreId = this.getStoreIdFromLocalStorage();
    if (defaultStoreId && !this.sessionFilter.storeId) {
      this.sessionFilter.storeId = defaultStoreId;
    }

    // Determine which to load based on activeTab
    if (this.activeTab === 'sessions') {
      this.loadSessions();
    } else {
      this.fetchStockTakes();
    }

    // Load Metadata
    this.loadStores();
    this.loadUsers();
  }

  loadStores() {
    this.inventoryService.getAllStores().subscribe({
      next: (res: any) => {
        const stores = res.data || res || [];
        if (Array.isArray(stores)) {
          this.stores = stores;
          stores.forEach((s: any) => {
            this.storesMap.set(s.id, s.name || s.storeName);
          });

          // Set default store if none selected
          if (!this.sessionFilter.storeId) {
            const defaultId = this.getStoreIdFromLocalStorage();
            if (defaultId) this.sessionFilter.storeId = defaultId;
          }
        }
      },
    });
  }

  loadUsers() {
    // Fetch all users (adjust limit if needed, or implement search on demand)
    this.usersService.getUsers(1, 1000).subscribe({
      next: (res: any) => {
        const users = res.data || res.users || []; // Adjust based on Users API response
        if (Array.isArray(users)) {
          users.forEach((u: any) => {
            this.usersMap.set(u.id, u.name || `${u.firstName} ${u.lastName}`);
          });
        }
      },
    });
  }

  getStoreName(id: string): string {
    return this.storesMap.get(id) || id || 'Unknown Store';
  }

  getUserName(id: string): string {
    return this.usersMap.get(id) || id || 'Unknown User';
  }

  switchTab(tab: 'sessions' | 'drafts') {
    this.activeTab = tab;
    if (tab === 'sessions' && this.sessions.length === 0) {
      this.loadSessions();
    } else if (tab === 'drafts' && this.stockTakes.length === 0) {
      this.fetchStockTakes();
    }
  }

  // --- SESSIONS LOGIC ---

  /** Map the active workflow view to server-side filters. */
  private viewFilters(): { status?: string; sessionType?: string } {
    switch (this.sessionView) {
      case 'toMerge':
        return { status: 'IN_PROGRESS', sessionType: 'REGULAR' };
      case 'toReview':
        return { status: 'IN_PROGRESS', sessionType: 'MASTER' };
      case 'completed':
        return { status: 'COMPLETED' };
      case 'merged':
        return { status: 'MERGED' };
      default:
        return { status: this.sessionFilter.status || undefined };
    }
  }

  setSessionView(view: 'all' | 'toMerge' | 'toReview' | 'completed' | 'merged') {
    if (this.sessionView === view) return;
    this.sessionView = view;
    this.sessionFilter.page = 1;
    this.selectedSessionIds.clear();
    this.loadSessions();
  }

  loadSessions() {
    this.loading = true;

    const params = {
      ...this.sessionFilter,
      ...this.viewFilters(),
    };

    this.stockTakeService.getSessions(params as any).subscribe({
      next: (res: any) => {
        this.sessions = res.data.map((s: any) => ({
          ...s,
          // Ensure ID is present if it comes as _id
          id: s.id || s._id,
        }));
        const total = res.total || 0;
        this.sessionPageCount =
          Math.ceil(total / this.sessionFilter.limit) || 1;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading sessions', err);
        // this.toastr.error('Failed to load sessions'); // Optional: silent fail if no sessions yet?
        this.loading = false;
      },
    });
  }

  toggleSelection(sessionId: string) {
    if (this.selectedSessionIds.has(sessionId)) {
      this.selectedSessionIds.delete(sessionId);
    } else {
      this.selectedSessionIds.add(sessionId);
    }
  }

  mergeSelected(content?: any) {
    if (this.selectedSessionIds.size < this.minMergeCount) {
      alert(`Select at least ${this.minMergeCount} session(s) to merge`);
      return;
    }

    this.previewLoading = true;
    const ids = Array.from(this.selectedSessionIds);

    // Fetch details for all selected sessions to get their entries
    const requests = ids.map((id) => this.stockTakeService.getSessionById(id));

    forkJoin(requests).subscribe({
      next: (sessions: any[]) => {
        this.generatePreview(sessions);
        this.previewLoading = false;
        if (content) {
          this.modalService.open(content, { size: 'xl' });
        }
      },
      error: (err) => {
        console.error('Error loading sessions for preview', err);
        this.toastr.error('Failed to load session details for preview');
        this.previewLoading = false;
      },
    });
  }

  generatePreview(sessions: any[]) {
    // Map of SKU -> { sku, description, sessions: { sessionId: qty }, total: qty }
    const paramsMap = new Map<string, any>();

    sessions.forEach((session) => {
      session.entries.forEach((entry: any) => {
        if (!paramsMap.has(entry.sku)) {
          paramsMap.set(entry.sku, {
            sku: entry.sku,
            description: entry.description,
            sessionQtys: {}, // Map sessionNumber -> qty
            total: 0,
          });
        }
        const item = paramsMap.get(entry.sku);
        // Add qty for this session (accumulate if multiple entries for same SKU in one session?)
        // Usually one entry per SKU per session, but to be safe:
        const sNum = session.stockTakeSessionNumber;
        item.sessionQtys[sNum] = (item.sessionQtys[sNum] || 0) + entry.quantity;
        item.total += entry.quantity;
      });
    });

    this.previewItems = Array.from(paramsMap.values());
  }

  confirmMerge(modal: any) {
    this.stockTakeService
      .mergeSessions({
        storeId: this.sessionFilter.storeId,
        sessionIds: Array.from(this.selectedSessionIds),
      })
      .subscribe({
        next: (masterSession) => {
          this.toastr.success('Merged successfully! Master Session Created.');
          this.selectedSessionIds.clear();
          this.loadSessions();
          modal.close();
        },
        error: (err) => {
          this.toastr.error(
            'Merge failed: ' + (err.error?.message || err.message),
          );
        },
      });
  }

  // Helper to get selected session objects for table header logic if needed
  getSelectedSessions() {
    // We only have IDs in selectedSessionIds, need to find names from this.sessions
    // But this.sessions (list) might have sparse info.
    // The preview logic fetches full details, so we use that or the list.
    return Array.from(this.selectedSessionIds).map((id) => {
      const s = this.sessions.find((x) => x.id === id);
      return s ? s.stockTakeSessionNumber : id;
    });
  }

  onSessionPageChange(page: number) {
    this.sessionFilter.page = page;
    this.loadSessions();
  }

  onSessionLimitChange(limit: number) {
    this.sessionFilter.limit = limit;
    this.onSessionPageSizeChange();
  }

  onPageSizeChange(limit: number) {
    this.pageSize = limit;
    this.pageSizeChange();
  }

  onSessionPageSizeChange() {
    this.sessionFilter.page = 1;
    this.loadSessions();
  }

  applySessionFilters() {
    // Changing the store/status/date filter can hide currently-selected
    // sessions; clear the selection so a merge can never span a store the user
    // has filtered away.
    this.selectedSessionIds.clear();
    this.sessionFilter.page = 1;
    this.loadSessions();
  }

  resetSessionFilters() {
    this.sessionFilter = {
      storeId: this.getStoreIdFromLocalStorage() || '',
      fromDate: null,
      toDate: null,
      status: '',
      page: 1,
      limit: 10,
    };
    this.loadSessions();
  }

  private getStoreIdFromLocalStorage(): string | null {
    const userString = localStorage.getItem('user');
    if (userString) {
      try {
        const userObject = JSON.parse(userString);
        // Prefer the store matching the ACTIVE location (header switcher) —
        // user.storeId is a login-time guess and, with a second warehouse,
        // an arbitrary one.
        const activeLocationId = localStorage.getItem('activeLocationId');
        const access: any[] = Array.isArray(userObject?.storeAccess)
          ? userObject.storeAccess
          : [];
        const activeMatch = access.find(
          (a: any) => a?.locationId === activeLocationId,
        );
        if (activeMatch?.storeId) return String(activeMatch.storeId);
        if (userObject && userObject.storeId) {
          return userObject.storeId;
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    return null;
  }

  // --- DRAFTS LOGIC (Existing) ---

  fetchStockTakes(): void {
    this.loading = true;

    const params: any = {
      q: this.searchTerm || undefined,
      mode: this.filterMode || undefined,
      limit: this.pageSize,
      page: this.currentPage,
    };

    this.stockTakeService.getStockTakeDrafts(params).subscribe({
      next: (response: any) => {
        this.stockTakes =
          response?.data?.map((st: any) => ({
            ...st,
            store: st.store || { name: '-' },
            location: st.location || { name: '-' },
            createdByUser: st.createdByUser || { name: '-' },
          })) || [];

        const total = response?.total || 0;
        this.pageCount = Math.ceil(total / this.pageSize) || 1;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching stock takes', err);
        this.toastr.error('Failed to load stock takes', 'Error');
        this.loading = false;
      },
    });
  }

  onSearch(): void {
    this.currentPage = 1;
    this.fetchStockTakes();
  }

  pageSizeChange(): void {
    this.currentPage = 1;
    this.fetchStockTakes();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.pageCount) return;
    this.currentPage = page;
    this.fetchStockTakes();
  }

  downloadStockTake(id: string): void {
    this.stockTakeService.downloadStockTakePDF(id).subscribe({
      next: (res: any) => {
        const blob = new Blob([res], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `StockTake-${id}.pdf`;
        a.click();
      },
      error: () => this.toastr.error('Failed to download PDF'),
    });
  }

  deleteStockTake(id: string): void {
    if (!confirm('Are you sure you want to delete this draft?')) return;

    this.stockTakeService.deleteStockTake(id).subscribe({
      next: () => {
        this.toastr.success('Stock take draft deleted successfully');
        this.fetchStockTakes();
      },
      error: () => {
        this.toastr.error('Failed to delete draft');
      },
    });
  }

  downloadTemplate(): void {
    this.stockTakeService.downloadTemplate().subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'stock-take-template.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);
        this.toastr.success('Template downloaded successfully!');
      },
      error: (error: any) => {
        console.error('Error downloading template:', error);
        this.toastr.error('Failed to download template.');
      },
    });
  }

  downloadVarianceReport(id: string): void {
    this.stockTakeService.downloadVarianceReport(id).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `VarianceReport-${id}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.toastr.success('Variance report downloaded successfully!');
      },
      error: (error: any) => {
        console.error('Error downloading variance report:', error);
        this.toastr.error('Failed to download variance report.');
      },
    });
  }

  /** Drafts / Uploads tab — same variance workbook as the sessions tab. */
  downloadDraftVarianceReport(draftId: string, stockTakeNumber?: string): void {
    this.stockTakeService.downloadDraftVarianceReport(draftId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `VarianceReport-${stockTakeNumber || draftId}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.toastr.success('Variance report downloaded successfully!');
      },
      error: (error: any) => {
        console.error('Error downloading variance report:', error);
        this.toastr.error('Failed to download variance report.');
      },
    });
  }

  downloadRegularReport(id: string): void {
    this.stockTakeService.downloadRegularReport(id).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `RegularReport-${id}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.toastr.success('Regular report downloaded successfully!');
      },
      error: (error: any) => {
        console.error('Error downloading regular report:', error);
        this.toastr.error('Failed to download regular report.');
      },
    });
  }
}
